<template>
    <ks-top-nav :fixed="true" :has-sidebar="true">
        <div class="brand">
            <img src="/images/fossa_logo_full.png" alt="Work Fossa" />
        </div>

        <transition name="slide-down">
            <span
                v-if="isMobile && unreadMentionsCount > 0"
                class="unread-count absolute flex jc-center align-center rounded-full bg-red-600 text-white text-xs shadow"
                style="top:1rem;right:1rem;"
                title="Notifications"
            >
                {{ unreadMentionsCount }}
            </span>
        </transition>

        <div class="collapse">
            <ul class="dropdown right">
                <li>
                    <router-link :to="{name: 'user-directory'}">Directory</router-link>
                </li>

                <li v-if="$userCan('reference_documents_view')">
                    <router-link :to="{name: 'reference-documents'}">References</router-link>
                </li>

                <li v-if="$userCanGroup('admin', 'certification')">
                    <router-link :to="{ name: 'admin-dashboard' }" >Admin</router-link>
                </li>

                <li v-if="is_impersonating">
                    <a
                        href="#"
                        style="background:#7746ec;height:40px;border-radius:5px;margin:0 0.25rem;"
                        @click.prevent="stopImpersonating"
                        class="unimpersonate-link"
                    >
                        <vault-icon :icon="icons.user" style="color:#fff;"></vault-icon>
                        <span class="text-white pl-1.5 tab-hide-up">Stop Impersonating</span>
                    </a>
                </li>

                <li v-if="canViewTasks" :class="activeLinkClasses('task-dashboard')">
                    <component
                        :is="$route.name === 'task-dashboard' ? 'a' : 'router-link'"
                        :to="{name: 'task-dashboard'}"
                        style="background: transparent;color: inherit"
                    >
                        <vault-icon :icon="icons.tasks"></vault-icon>
                    </component>
                </li>

                <li>
                    <a href="#" class="notifications-dropdown-toggle relative" @click.prevent.stop="clickNotifications">
                        <template v-if="isMobile">
                            Notifications
                        </template>
                        <template v-else>
                            <vault-icon :icon="icons.notifications" :class="{ 'muted': unreadMentionsCount === 0 }"></vault-icon>
                        </template>
                        <transition name="slide-down">
                            <span
                                v-if="unreadMentionsCount > 0"
                                class="unread-count ml-2 px-0.5 flex jc-center align-center rounded-full bg-red-600 text-white text-xs shadow"
                                :class="{ 'absolute unread-count-position': !isMobile }"
                                title="Notifications"
                            >
                                {{ unreadMentionsCount }}
                            </span>
                        </transition>
                    </a>

                    <!-- MOBILE NOTIFICATIONS -->
                    <ul v-if="isMobile" class="sub-dropdown notifications-list relative mobile">
                        <li v-for="mention in mentions" :key="mention.mention_id">
                            <mention-link
                                :mention="mention"
                                class="mention-link text-xs"
                                :class="{ 'muted': !isMobile && mention.read_at }"
                            ></mention-link>
                        </li>
                    </ul>

                    <!-- DESKTOP NOTIFICATIONS -->
                    <div
                        v-else
                        v-show="show_notifications_dropdown"
                        ref="notificationsDropdown"
                        class="absolute notifications-list relative notifications-position shadow"
                    >
                        <div class="sticky shadow-sm px-2.5 pt-2 pb-1.5 text-xs uppercase bg-gray-100">
                            <template v-if="mentions.length === 0">
                                No Notifications
                            </template>
                            <span v-else class="pointer text-blue-900 hover:text-blue-700" @click.prevent="openNotificationsModal">
                                All Notifications
                            </span>
                        </div>

                        <div v-for="mention in mentions" :key="mention.mention_id">
                            <mention-link
                                :mention="mention"
                                class="mention-link block text-xs"
                                :class="{ 'muted': !isMobile && mention.read_at }"
                            ></mention-link>
                        </div>
                    </div>
                </li>

                <li>
                    <a href="#" class="dropdown-toggle">
                        <avatar
                            v-if="isMultiEntity"
                            :value="entity.entity_name"
                            :initials="entity.entity_initials || null"
                            :seed="`entity:${entity_id}`"
                            class="mr-2 h-8 w-8 rounded-full text-white"
                        ></avatar>
                        {{ username }}
                    </a>

                    <ul class="sub-dropdown" style="z-index:9999">
                        <template v-if="isMultiEntity && !is_impersonating">
                            <li v-for="entity in otherEntities" :key="entity.entity_id" class="dropdown-item">
                                <a href="#" @click.prevent="switchEntity(entity)">
                                    <avatar
                                        :value="entity.entity_name"
                                        :initials="entity.entity_initials || null"
                                        :seed="`entity:${entity.entity_id}`"
                                        class="-ml-1 mr-2 h-8 w-8 rounded-full text-white"
                                    ></avatar>
                                    {{ entity.entity_name }}
                                </a>
                            </li>
                        </template>

                        <li>
                            <router-link :to="{ name: 'user-profile' }">
                                <vault-icon :icon="icons.profile" class="w-6 mr-3"></vault-icon>
                                Profile
                            </router-link>
                        </li>

                        <li v-if="showTimesheetLink">
                            <router-link :to="{ name: 'timesheets' }">
                                <vault-icon :icon="icons.timesheets" class="w-6 mr-3"></vault-icon>
                                Timesheets
                            </router-link>
                        </li>

                        <li v-if="is_system_admin">
                            <a href="#" @click.prevent="switchAppType">Switch to {{ nextAppType }} UI</a>
                        </li>

                        <li>
                            <a href="#" @click.prevent="logout">
                                <vault-icon :icon="icons.logout" class="w-6 mr-3"></vault-icon>
                                Logout
                            </a>
                        </li>
                    </ul>
                </li>
            </ul>
        </div>

        <ks-modal
            ref="notificationsModal"
            title="All Notifications"
            :loading="loading_mentions"
            min-width="500px"
            max-width="500px"
        >
            <div ref="mentionsContent" :style="mentionsContentStyle">
                <ks-simple-paginator :pagination="paginator" @input="fetchMentions"></ks-simple-paginator>

                <mention-link
                    v-for="mention in all_mentions"
                    :key="mention.mention_id"
                    :mention="mention"
                    reference-class="text-xs"
                    body-class="mt-1"
                    :class="{ 'bg-gray-50': mention.read_at }"
                    class="block p-2 hover:bg-gray-100"
                    @navigate="$refs.notificationsModal.close()"
                ></mention-link>
            </div>
        </ks-modal>
    </ks-top-nav>
</template>

<script>
    // External Dependencies
    import { mapActions, mapGetters } from 'vuex';
    import api from 'kickstart-ui/lib/api';

    // Icons
    import { faAddressCard } from '@fortawesome/pro-regular-svg-icons/faAddressCard';
    import { faArrowRightFromBracket } from '@fortawesome/pro-regular-svg-icons/faArrowRightFromBracket';
    import { faBell } from '@fortawesome/pro-regular-svg-icons/faBell';
    import { faCheckSquare } from '@fortawesome/pro-regular-svg-icons/faCheckSquare';
    import { faExchange } from '@fortawesome/pro-duotone-svg-icons/faExchange';
    import { faListTimeline } from '@fortawesome/pro-duotone-svg-icons/faListTimeline';
    import { faUserSecret } from '@fortawesome/pro-solid-svg-icons/faUserSecret';

    // Mixins
    import AppInfoMixin from '@/mixins/AppInfoMixin.js';
    import ImpersonateMixin from '@Vault/Admin/Users/mixins/ImpersonateMixin';
    import MentionsMixin from '../UI/mixins/MentionsMixin.js';
    import UserInfoMixin from '@Vault/Users/mixins/UserInfoMixin';
    import WatchesViewport from '../UI/mixins/WatchesViewport.js';

    // Repositories
    import Timesheets from '@Vault/Timesheets/repositories/TimesheetRepository';

    // Components
    import Avatar from 'UI/Avatar.vue';
    import MentionLink from '../UI/MentionLink.vue';

    export default {
        name: 'TopMenu',

        mixins: [
            AppInfoMixin,
            ImpersonateMixin,
            MentionsMixin,
            UserInfoMixin,
            WatchesViewport,
        ],

        computed: {
            ...mapGetters([
                'username',
                'is_impersonating',
                'mentions',
                'unreadMentionsCount',
                'user_id',
                'has_task_categories',
                'entity',
                'entity_id',
                'user_entities',
            ]),

            isMultiEntity() {
                return this.user_entities.length > 1;
            },

            showTimesheetLink() {
                if ( this.isField || this.isAdmin || this.isAccountAdmin) {
                    return this.categories.length > 0;
                }

                return false;
            },

            isMobile() {
                return ['xs', 'sm'].includes(this.screenWidth);
            },

            mentionsContentStyle() {
                return {
                    minHeight: this.mentions_content_height,
                    maxHeight: 'calc(100vh - 80px)',
                    overflowY: 'auto',
                };
            },

            canViewTasks() {
                if ( !this.has_task_categories ) {
                    return false;
                }

                return this.isAdmin || this.isAccountAdmin;
            },

            activeLinkClasses() {
                return (name) => {
                    return {
                        'bg-blue-700': this.$route.name === name,
                        'text-white': this.$route.name === name
                    }
                }
            },

            otherEntities() {
                return this.user_entities.filter(({entity_id}) => entity_id !== this.entity_id);
            },

            nextAppType() {
                return {
                    work: 'form',
                    form: 'work',
                }[this.app_type];
            },
        },

        data() {
            return {
                userIcon: faUserSecret,
                notification_icon: faBell,
                task_icon: faCheckSquare,
                icons: {
                    user: faUserSecret,
                    notifications: faBell,
                    tasks: faCheckSquare,
                    switch: faExchange,
                    logout: faArrowRightFromBracket,
                    profile: faAddressCard,
                    timesheets: faListTimeline,
                },

                categories: [],

                loading_mentions: false,
                all_mentions: [],
                paginator: {},
                mentions_content_height: 0,
                show_notifications_dropdown: false,
            }
        },

        beforeMount() {
            Timesheets.get('categories', {enabled: 1})
                .then(({data}) => this.categories = data);
        },

        mounted() {
            document.addEventListener('click', this.handleClick);
        },

        beforeDestroy() {
            document.removeEventListener('click', this.handleClick);
        },

        methods: {
            ...mapActions([
                'setAppType',
                'switchingEntities',
            ]),

            switchAppType() {
                this.setAppType(this.nextAppType);
            },

            async switchEntity({entity_id, entity_name}) {
                this.switchingEntities();

                await this.$nextTick();

                api.post('/switch-entity', { entity_id }, { baseURL: '/'})
                    .then(() => {
                        window.location.href = '/app/dashboard';
                    })
                    .catch(error => {
                        console.error(error);

                        this.$toast({
                            type: 'error',
                            message: `Error switching to ${entity_name}.`
                        });
                    })
                    .finally(() => {
                        this.switchingEntities(false);
                    });
            },

            handleClick(event) {
                if ( !this.$refs.notificationsDropdown?.contains(event.target) ) {
                    this.show_notifications_dropdown = false;
                }
            },

            logout() {
                api.post('/logout', {}, { baseURL: '/' })
                    .then(({data}) => {
                        window.location.href = '/';
                    })
                    .catch(({response}) => {
                        console.error('DA:ERR: ', response);
                    })
            },

            clickNotifications() {
                if ( this.isMobile ) {
                    this.openNotificationsModal();
                } else {
                    this.show_notifications_dropdown = !this.show_notifications_dropdown;
                }
            },

            openNotificationsModal() {
                this.$refs.notificationsModal.open();

                this.fetchMentions();

                this.show_notifications_dropdown = false;
            },

            fetchMentions(page = 1) {
                this.loadMentions('user', this.user_id, { page })
                    .then(({data, paginator}) => {
                        this.all_mentions = data;
                        this.paginator = paginator;

                        this.$nextTick(() => {
                            const height = this.$refs.mentionsContent?.clientHeight;
                            if ( height > parseInt(this.mentions_content_height) ) {
                                this.mentions_content_height = height + 'px';
                            }
                        });
                    });
            },
        },

        components: {
            Avatar,
            MentionLink,
        }
    }
</script>

<style>
    .notifications-list {
        max-height: 18rem;
        overflow-y: auto;
    }
    .notifications-list.mobile {
        max-height: 13rem;
    }
    .notifications-position {
        top: 60px;
        right: 0;
        width: 24rem !important;
    }
    .notifications-dropdown-toggle {
        padding-left: 0.75rem !important;
        padding-right: 0.75rem !important;
    }
    .unread-count {
        min-width: 1rem;
        min-height: 1rem;
    }
    .unread-count-position {
        right: 0.125rem;
        top: 0.875rem;
    }
</style>
